import type { FormArray, FormControl, FormGroup } from '@angular/forms';
import { TheftReportCase } from './report-portal';
import { CombinedCancellationTimelineEvent, LiveCamera } from './transaction';

export type TheftTypeFormGroup = FormGroup<{
  is_cancellation: FormControl<boolean>;
  is_underringing: FormControl<boolean>;
  is_walkout: FormControl<boolean>;
  is_cashier: FormControl<boolean>;
}>;

export interface IncidentFormControls {
  isTheft: FormControl<boolean>;
  reviewOutcomeId: FormControl<string | null>;
  suspectedItems: FormArray<FormControl<TransactionItemTiny>>;
  matchingCase: FormControl<string>;
  additionalStolenValue: FormControl<number>;
  instance_at: FormControl<Date>;
  theftType: TheftTypeFormGroup;
  uniqueInstancePhoto: FormArray<FormControl<File>>;
  existingUniqueInstancePhoto: FormArray<FormControl<string>>;
  reports: FormArray<FormControl<File>>;
  existingReports: FormArray<FormControl<string>>;
  extraLinks: FormArray<FormControl<string>>;
  didNotReturn: FormControl<boolean>;
  selectedImages: FormArray<FormControl<string>>;
  useKioskPhoto: FormControl<boolean>;
}

export interface ValidationAssignmentControls extends IncidentFormControls {
  commentsRating: FormControl<number>;
}

export interface FindingsControls {
  // findings
  describeClothing: FormControl<string>;
  describeSnackDrink: FormControl<string>;
  numPaidItems: FormControl<number>;
  numUnpaidItems: FormControl<number>;
  numTotalItems: FormControl<number>;
  estimatedTotal: FormControl<string>;

  numPaymentAttempts: FormControl<number>;
  paymentMethod: FormControl<string>;

  howManyItemsScanned: FormControl<string>;
  kioskInteractionResult: FormControl<string>;

  numSnacksDrinksSelected: FormControl<number>;
  numItemsScanned: FormControl<number>;
  totalActualItemsObserved: FormControl<number>;
  numItemsOverlooked: FormControl<number>;
  numItemsSelectedAfterTransaction: FormControl<number>;
  kioskScannedItems: FormControl<string | null>;
  kioskUnscannedItems: FormControl<string | null>;

  costOfFalseItem: FormControl<string>;
  numSnacksDrinksRemoved: FormControl<number>;

  comments: FormControl<string>;
  ntfReason: FormControl<NtfReasonOption | null>;
}

export type IncidentFormGroup = FormGroup<IncidentFormControls>;
export type ValidationFormGroup = FormGroup<ValidationAssignmentControls>;
export type FindingsFormGroup = FormGroup<FindingsControls>;

export type IncidentFormValues = {
  isTheft: boolean;
  reviewOutcomeId: string | null;
  comments?: string;
  commentsRating: number;
  suspectedItems: TransactionItemTiny[];
  matchingCase: string;
  additionalStolenValue: number;
  instance_at: Date | null;
  theftType: TheftType;
  uniqueInstancePhoto: File[] | null;
  existingUniqueInstancePhoto: string[];
  reports: File[] | null;
  existingReports: string[];
  extraLinks: string[];
  didNotReturn: boolean;
  selectedImages?: string[];
  useKioskPhoto?: boolean;
};

export type Review = {
  id: string;
  user_id?: number;
  external_identity_id?: string;
  external_user_id?: string;
  location_id: number;
  review_subject: ReviewSubjectType;
  subject_keystore?: string;
  subject_keystore_parsed: {
    id: string | null;
    notes?: string;
  };
  status: string;
  assigned_at: Date;
  expires_at: Date;
  completed_at?: Date;
  events?: string;
  case_images: CaseImage[];
  instructions: string;
  rhombus_password_opt_out?: boolean;
  email?: string;
};

export type ReviewOutcomeKeystore = {
  is_theft?: boolean;
  theft_type?: TheftType;
  date_shared?: string;
  instance_num?: number;
  repeat_offender?: boolean;
  item_cancellation_value?: number;
  is_self_service?: boolean;
  is_hidden?: boolean;
  use_kiosk_photo?: boolean;
  annotations?: Annotation[];
  terminal_id?: string;
};

export type ReviewOutcome = {
  keystore?: string;
  keystore_parsed?: ReviewOutcomeKeystore;
  review_outcome_id: string;
  review_id?: string;
  location_id?: number;
  location_timezone?: string;
  comments?: string;
  case_id?: string;
  instance_at?: Date;
  attachments?: string;
  attachments_parsed?: Array<{
    link_type: 'VideoLink' | 'InstancePhoto' | 'ReportImage' | 'RecentWalkInSnapshot';
    link: string;
  }>;
  // additional information injected by some of our queries
  review_subject?: string;
  created_at?: string;
  email?: string;
};

export type TheftType = {
  is_cancellation: boolean;
  is_underringing: boolean;
  is_walkout: boolean;
  is_cashier: boolean;
  custom?: boolean;
};

export type Annotation = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  start_time?: number;
  end_time?: number;
  incident_type: string;
  item_description: string;
  item_price: number;
};

export type ReviewOutcomePayload = {
  is_theft: boolean;
  case_id: string | null;
  report_links: string[];
  photos_links: string[];
  extra_links: { link: string }[];
  theft_type: TheftType;
  suspected_items: { id: number; total: number }[];
  instance_at: string;
  comments: string;
  additional_stolen_value: number;
  recent_walk_in_ids?: string[];
  use_kiosk_photo?: boolean;
  annotations?: Annotation[];
};

export type SubmitReviewRequest = ReviewOutcomePayload & {
  complete?: boolean;
};

export type UpdateReviewOutcomeRequest = ReviewOutcomePayload;

export type SubmitValidationReviewRequest = SubmitReviewRequest & {
  no_changes: boolean;
  comments_rating?: number;
};

export type SubmitErrorRequest = {
  comments: string;
  error_message: string;
  errors: {
    is_blocking: boolean;
    review_error_type: string;
  }[];
};

export type ReviewTemplateType = 'Underringing' | 'Cancellation' | 'Walkout';

export enum ReviewSubjectType {
  Cancellation = 'Cancellation',
  Validation = 'Validation',
  Sideloaded = 'Sideloaded',
  Walkout = 'Walkout',
  SelfService = 'self-service',
  Deepdive = 'Deepdive',
  Underringing = 'Underringing',
  Grocery = 'Grocery',
}

export enum ReviewStatus {
  Created = 'created',
  Assigned = 'assigned',
  Pending = 'pending',
  Completed = 'completed',
  Validated = 'validated',
  Abandoned = 'abandoned',
  Error = 'error',
}

export enum ReviewErrorTypeEnum {
  CamerasOffline = 'CamerasOffline',
  CamerasBadAngle = 'CamerasBadAngle',
  CamerasLowBandwidth = 'CamerasLowBandwidth',
  TimeDifference = 'TimeDifference',
  MarketReport = 'MarketReport',
  MarketReportNoAccess = 'MarketReportNoAccess',
  MarketReportMissingTransactions = 'MarketReportMissingTransactions',
  Other = 'Other',
}

export type LocationFlagReason = {
  id: ReviewErrorTypeEnum;
  description: string;
};

// ================================================================================================
// Cancellation types
// ================================================================================================

export type CancelledItems = {
  id: number;
  unique_id: string;
  item_unique_id: string | null;
  barcode: string | null;
  description: string;
  cost: number;
  price: number;
  tax1: number;
  tax2: number;
  deposit: number;
  total: number;
  cancel_date: string;
  name: string;
  branch: string;
  cancel_screen: string;
  user_name: string | null;
  created_at: string;
  data_source: string;
};

export type CancellationDetails = {
  cancel_date: string;
  name: string;
  branch: string;
  location_id: number;
};

export type TransactionDetailItem = {
  description: string;
  total: number;
};

// new generic one
export type InvestigationTransactionDetails = {
  transaction_id: string;
  platform_id?: string;
  occurred: 'before' | 'after' | 'cancellation' | 'walkout';
  items: TransactionDetailItem[];
  trans_date: Date;
};

export type SoldTransactionDetails = {
  occurred: 'before' | 'after';
  // I don't really know, new data returns number, but old type says string
  transaction_id: string | number;
  platform_id?: string;
  time_sold: string;
  items: TransactionDetailItem[];
};

export type CancellationComment = {
  id: number | null;
  created_at: string;
  content: string;
  user_name: string;
  cancellation_id: number;
  user_id: number;
};

export type CaseImage = {
  case_id: string;
  urls: string[];
};

export interface VideoClip {
  id: string;
  link: string;
  cancellation_id: number;
  offset_seconds: number;
  created_at: string;
  review_id?: string;
  not_useful_submitted: boolean;
  is_auto_download?: boolean;
}

export type VideoSnapshot = {
  id: string;
  image_timestamp: number;
  link: string;
};

// Payload for adding video snapshots to a cancellation
export interface VideoSnapshotUpload {
  link: string;
  image_timestamp: number; // unix ms timestamp
  video_clip_id: string;
}

export interface AddCancellationVideoSnapshotsRequest {
  video_snapshots: VideoSnapshotUpload[];
}

export type BaseTransactionDetails = {
  image_id: string;
  live_streams: LiveCamera[];
  location_id: number | null;
  review_expires_at: string;
  case_images: CaseImage[];
  review_status: string;
  case?: TheftReportCase;
  is_self_service?: boolean;
  photos: string[];
  instructions: string;
};

export type Cancellation = BaseTransactionDetails & {
  cancellation: {
    details: CancellationDetails;
    items: CancelledItems[];
    shared: {
      uuid: string | null;
    };
  };
  timeline: SoldTransactionDetails[];
  recent_walk_ins?: VideoSnapshot[];
  video_clips: VideoClip[];
  combined_timeline?: CombinedCancellationTimelineEvent[];
};

// ================================================================================================
// Walkout types
// ================================================================================================

export type WalkoutComment = {
  id: number | null;
  created_at: string;
  content: string;
  user_name: string;
  walkout_id: number;
  user_id: number;
};

export type WalkoutTransactionDetails = {
  occurred: 'before' | 'after' | 'walkout';
  transid: string | number;
  transdate: string;
  items: string;
};

export interface WalkoutVideoClip {
  id: string;
  link: string;
  walkout_id: number;
  offset_seconds: number;
  created_at: string;
  not_useful_submitted: boolean;
  is_auto_download: boolean;
}

export type WalkoutDetails = {
  walkout_date: Date;
  name: string;
  branch: string;
  location_id: number;
};

export type Walkout = BaseTransactionDetails & {
  image_id: string;
  walkout: WalkoutDetails;
  timeline: WalkoutTransactionDetails[];
  video_clips: WalkoutVideoClip[];
  matching_transaction: boolean;
  comments: WalkoutComment[];
};

// ================================================================================================
// Sideloaded types
// ================================================================================================

export type Sideloaded = {
  id: string;
  user_id: string | null;
  location_id: number;
  review_subject: string;
  subject_keystore: string;
  subject_keystore_parsed: ReviewSubjectKeystore;
  status: string;
  assigned_at: string;
  expires_at: string;
  completed_at: string;
  case_images: CaseImage[];
  instructions: string;
  live_streams: LiveCamera[];
};

export type ReviewSubjectKeystore = {
  id: string | null;
  notes: string;
};

// if cancellation, we include extra properties
export type ValidationCancellation = Cancellation & {
  review_id: string;
};

export type ValidationWalkout = Walkout & {
  review_id: string;
};

export type ValidationSideloaded = Sideloaded;

export type Validation = ValidationCancellation | ValidationSideloaded | ValidationWalkout;

export type TransactionItemTiny = {
  id?: number;
  description: string;
  price?: number;
  total?: number;
};

export type AssignmentTimelineEntry = {
  timestamp: string;
  transaction_id?: string;
  platform_id?: string;
  occurred: string;
  items: TransactionItemTiny[];
};

export type AssignmentTransaction = {
  id?: string;
  date?: string;
  total?: number;
  items: TransactionItemTiny[];
};

export type TransactionDetailsTiny = {
  transaction?: AssignmentTransaction;
  timeline: AssignmentTimelineEntry[];
};

export type TransactionEventData = {
  item_code?: string;
  item_name?: string;
  total?: number;
  price?: number;
};

export type TransactionEvent = {
  id: number;
  event_time: string;
  event_type: string;
  details?: {
    event_time?: string;
    event_type?: string;
    terminal?: string;
    ticket_no?: number;
    cashier_id?: string;
    data?: TransactionEventData | null;
  } | null;
};

export type AssignmentDetails = {
  assignment_id: string;
  subject_type: string;
  subject_id?: string;
  status: string;
  expires_at: string | null;
  location_id: number;
  location_name: string;
  location_timezone?: string;
  branch_name: string;
  instructions: string;
  rhombus_password_opt_out?: boolean;
  user_id?: string | null;
  actor: string;
  is_self_service: boolean;
  notes: string;
  case?: TheftReportCase;
  transaction_details?: TransactionDetailsTiny;
  live_streams: LiveCamera[];
  case_images: CaseImage[];
  video_clips: VideoClip[];
  video_snapshots: VideoSnapshot[];
  photos: string[];
  transaction_events?: TransactionEvent[];
  email?: string;
  completed_at?: string;
};

// other types

export type SoldItem = {
  occurred: string;
  date: string;
  items: string[];
};

export type ReviewAssignmentHasAnyDto = {
  has_any_reviews: boolean;
};

export type ReviewAssignmentDto = {
  review_id: string;
  review_subject_type: string;
  expires_at: string;
};

export enum IncidentFormError {
  COMMENTS_REQUIRED = 'commentsRequired',
  COMMENTS_MAX_LENGTH = 'commentsMaxLength',
  COMMENTS_MIN_LENGTH = 'commentsMinLength',
  THEFT_TYPE_REQUIRED = 'theftTypeRequired',
  INSTANCE_PHOTO_REQUIRED = 'instancePhotoRequired',
  EXTRA_LINKS_REQUIRED = 'extraLinksRequired',
  EXTRA_LINKS_DUPLICATE = 'duplicateExtraLinks',
  INSTANCE_AT_REQUIRED = 'instanceAtRequired',
  INSTANCE_AT_INVALID = 'instanceAtInvalid',
  INSTANCE_AT_OUT_OF_RANGE = 'instanceAtOutOfRange',
  COMMENTS_RATING_REQUIRED = 'commentsRatingRequired',
  ASSIGNED_TO_OTHER = 'assignedToOther',
}

// ================================================================================================
// Admin Create Review helper types
// ================================================================================================

// Result of creating a cancellation via Transactions/Cancellations
export type CreatedCancellation = { id: number };

// Internal flow result used when chaining creation + assignment + upload
export type CreateFlowResult = {
  created: CreatedCancellation;
  assignment: ReviewAssignmentDto;
};

export interface ReviewTemplateFormField {
  type: 'text' | 'number' | 'select';
  formControlName: string;
  label: string;
  placeholder: string;
  required?: boolean;
  options?: string[];
}

export interface ReviewTemplateResult {
  keys: string[];
  template: string;
}

export interface ReviewTemplate {
  appliesToTypes: ReviewTemplateType[];
  template: string;
  fields: ReviewTemplateFormField[];
  results: ReviewTemplateResult[];
}

export interface IncidentFormContext {
  locationId?: number;
  instanceAtDate?: Date;
  form: IncidentFormGroup;
}

export interface SideloadedIncidentFormContext extends IncidentFormContext {
  isAssignedToOther: boolean;
}

export interface UploadVideoClipsRequest {
  filePath: string;
  cancellationUniqueId: string;
  reviewId?: string;
  cancellationOffsetSeconds?: number;
}

// ================================================================================================
// No Theft Found (NTF) Reason Types
// ================================================================================================

export interface ProblemReason {
  id: string;
  label: string;
  code: string | null;
}

export interface NtfReasonOption {
  label: string;
  category: string;
}

// ================================================================================================
// Load More Transactions Types
// ================================================================================================

export interface GetMoreTransactionsResult {
  transactions: SoldTransactionDetails[];
  has_more: boolean;
  last_transaction_time: string | null;
}

export type TimelineRow = {
  when: string;
  transactionId?: string;
  timestamp: string;
  items: string;
  highlight: boolean;
};

export interface NtfReason {
  id: string;
  parent_id: string | null;
  label: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface NtfReasonCategory extends NtfReason {
  parent_id: null;
  reasons: NtfReason[];
}
